import {
  Button,
  Box,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { ArchiveMaterial } from './ArchiveMaterial';
import { useTranslation } from 'react-i18next';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { materialTableColumns } from './Columns';
import { EditMaterial } from './EditMaterial';
import { CreateMaterial } from './CreateMaterial';
import { MaterialComplianceDocuments } from './complianceDocuments/MaterialComplianceDocuments';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { LoadingOverlay } from '@texas/components/shared/LoadingOverlay';
import { DataTableContainer } from '@texas/components/shared/dataTable/DataTableContainer';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { PagedItems, ServerError } from '@texas/types';
import { useStableCallback } from '@texas/hooks/useStableCallback';
import { request } from '@texas/utils/helpers/httpHelpers';
import { ConfigurationPage } from '@texas/components/configuration/layout/ConfigurationPage';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { complianceDocumentDefinitionsApi } from '@texas/api/endpoints/metadata/complianceDocumentDefinitionsApi';
import { materialComplianceDocumentEvents } from './complianceDocuments/events';
import { hasValidMaterialPercentages } from './mixedMaterial';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';
import { TexasSwitch } from '@texas/components/shared/form/TexasSwitch';
import {
  Material,
  SearchQuery,
  materialsApi,
} from '@texas/api/endpoints/metadata/materialsApi';

interface MaterialTableProps {
  materials: PagedItems<Material> | null;
  columns: ReturnType<typeof materialTableColumns>;
  isMixed: boolean;
  sortBy: string;
  sortDesc: boolean;
  page: number;
  pageSize: number;
  error: ServerError | null;
  onSortChange: (sorting: { key: string; desc: boolean }) => void;
  onPageChange: (page: number) => void;
}

const materialPageSize = 25;

export function Materials() {
  const complianceDisclosure = useValueDisclosure<Material>();
  const {
    onOpen: onCreateOpen,
    onClose: onCreateClose,
    isOpen: isCreateOpen,
  } = useDisclosure();
  const {
    value: archiveValue,
    isOpen: isArchiveOpen,
    onOpen: onArchiveOpen,
    onClose: onArchiveClose,
  } = useValueDisclosure<Material>();
  const {
    value: editValue,
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useValueDisclosure<number>();

  const { t } = useTranslation();
  const toast = useToast();

  const {
    data: materials,
    refetch,
    error,
    loading,
  } = useApiResource(materialsApi.search);

  const {
    data: definitions,
    refetch: fetchDefinitions,
    loading: definitionsLoading,
  } = useApiResource(complianceDocumentDefinitionsApi.getAll);

  useEffect(() => {
    fetchDefinitions({
      searchTerm: '',
      includeArchived: true,
      sortBy: 'name',
      sortDesc: false,
    });
  }, [fetchDefinitions]);

  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    page: 1,
    pageSize: materialPageSize,
    isMixed: true,
    includeArchived: false,
    searchTerm: '',
    sortBy: 'code',
    sortDesc: false,
  });

  const { request: restoreMaterialRequest } = useApiRequest(
    materialsApi.restore,
  );

  useEffect(() => {
    refetch(searchQuery);
  }, [refetch, searchQuery]);

  const definitionOptions = useMemo(
    () =>
      (definitions ?? []).map((definition) => ({
        value: definition.id,
        label: definition.name,
      })),
    [definitions],
  );

  const openComplianceMaterialId = complianceDisclosure.isOpen
    ? complianceDisclosure.value?.id
    : undefined;

  useEffect(() => {
    return materialComplianceDocumentEvents.changed.subscribe((materialId) => {
      if (
        materialId === openComplianceMaterialId ||
        materials?.items.some((material) => material.id === materialId)
      ) {
        refetch(searchQuery);
      }
    });
  }, [materials, openComplianceMaterialId, refetch, searchQuery]);

  const performRestore = useStableCallback(async (id: number) => {
    await request(
      restoreMaterialRequest,
      [id],
      () => {
        toast({
          title: t('general.restored'),
          status: 'success',
          isClosable: true,
        });
        refetch(searchQuery);
      },
      (error: ServerError) => {
        toast({
          title: t('general.restoreFailed'),
          description: error.message,
          status: 'error',
          isClosable: true,
        });
      },
    );
  });

  const mixedColumns = useMemo(() => {
    return materialTableColumns(
      t,
      true,
      onArchiveOpen,
      performRestore,
      onEditOpen,
      complianceDisclosure.onOpen,
    );
  }, [
    t,
    onArchiveOpen,
    performRestore,
    onEditOpen,
    complianceDisclosure.onOpen,
  ]);
  const columns = useMemo(() => {
    return materialTableColumns(
      t,
      false,
      onArchiveOpen,
      performRestore,
      onEditOpen,
      complianceDisclosure.onOpen,
    );
  }, [
    t,
    onArchiveOpen,
    performRestore,
    onEditOpen,
    complianceDisclosure.onOpen,
  ]);

  useEffect(() => {
    if (!materials || loading) return;
    const lastPage = Math.max(
      1,
      Math.ceil(materials.totalItems / searchQuery.pageSize),
    );
    if (searchQuery.page > lastPage) {
      setSearchQuery((current) => ({ ...current, page: lastPage }));
    }
  }, [materials, loading, searchQuery.page, searchQuery.pageSize]);

  const onSortChange = useCallback(
    ({ key, desc }: { key: string; desc: boolean }) => {
      setSearchQuery((current) => ({
        ...current,
        sortBy: key,
        sortDesc: desc,
        page: 1,
      }));
    },
    [],
  );
  const onPageChange = useCallback((page: number) => {
    setSearchQuery((current) => ({ ...current, page }));
  }, []);

  const table = (
    <MaterialTable
      materials={materials}
      columns={searchQuery.isMixed ? mixedColumns : columns}
      isMixed={searchQuery.isMixed === true}
      sortBy={searchQuery.sortBy}
      sortDesc={searchQuery.sortDesc}
      page={searchQuery.page}
      pageSize={searchQuery.pageSize}
      error={error}
      onSortChange={onSortChange}
      onPageChange={onPageChange}
    />
  );

  return (
    <ConfigurationPage
      title={t('general.materials')}
      toolbar={
        <>
          <SearchFilterInput
            value={searchQuery.searchTerm}
            style="entityDirectory"
            onChange={(s) =>
              setSearchQuery({ ...searchQuery, searchTerm: s, page: 1 })
            }
          />
          <Box w={{ base: 'full', md: '320px' }}>
            <TexasSelect
              aria-label={t('materialComplianceDocuments.configurationFilter')}
              placeholder={t('materialComplianceDocuments.allConfigurations')}
              minHeight="44px"
              isClearable={true}
              isLoading={definitionsLoading}
              options={definitionOptions}
              value={
                definitionOptions.find(
                  (option) =>
                    option.value === searchQuery.complianceDocumentDefinitionId,
                ) ?? null
              }
              onChange={(option) =>
                setSearchQuery({
                  ...searchQuery,
                  complianceDocumentDefinitionId: option?.value,
                  page: 1,
                })
              }
            />
          </Box>
          <TexasSwitch
            checked={searchQuery.includeArchived}
            label={t('general.includeArchived')}
            onChange={(checked) =>
              setSearchQuery({
                ...searchQuery,
                includeArchived: checked,
                page: 1,
              })
            }
          />
        </>
      }
      action={
        <Button variant="entityDirectoryPrimary" onClick={onCreateOpen}>
          {t('general.addNew')}
        </Button>
      }
    >
      {complianceDisclosure.isOpen && complianceDisclosure.value && (
        <MaterialComplianceDocuments
          key={complianceDisclosure.value.id}
          material={complianceDisclosure.value}
          onClose={complianceDisclosure.onClose}
        />
      )}
      {archiveValue && (
        <ArchiveMaterial
          material={archiveValue}
          isOpen={isArchiveOpen}
          onClose={onArchiveClose}
          onArchive={() => {
            refetch(searchQuery);
          }}
        />
      )}

      <EditMaterial
        materialId={editValue}
        isOpen={isEditOpen}
        onClose={onEditClose}
        onUpdated={(_) => {
          refetch(searchQuery);
        }}
      />

      <CreateMaterial
        onCreated={() => {
          refetch(searchQuery);
          onCreateClose();
        }}
        onClose={onCreateClose}
        isOpen={isCreateOpen}
      />

      <VStack
        w="100%"
        alignItems="start"
        position="relative"
        minH={loading ? 24 : undefined}
      >
        {loading && <LoadingOverlay />}
        <Tabs
          w="100%"
          isLazy={true}
          index={searchQuery.isMixed ? 0 : 1}
          onChange={(index) =>
            setSearchQuery((current) => ({
              ...current,
              isMixed: index === 0,
              page: 1,
            }))
          }
        >
          <TabList>
            <Tab>{t('general.mixedMaterails')}</Tab>
            <Tab>{t('general.materials')}</Tab>
          </TabList>
          <TabPanels>
            <TabPanel>{table}</TabPanel>
            <TabPanel>{table}</TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </ConfigurationPage>
  );
}

const MaterialTable = memo(function MaterialTable({
  materials,
  columns,
  isMixed,
  sortBy,
  sortDesc,
  page,
  pageSize,
  error,
  onSortChange,
  onPageChange,
}: MaterialTableProps) {
  return (
    <DataTableContainer
      w="100%"
      datatable={{
        data: materials?.items ?? [],
        sorted: {
          onSortedChange: onSortChange,
          key: sortBy,
          desc: sortDesc,
        },
        columns,
        rowProps: {
          getRowId: (material) => String(material.id),
          uniqueRowStyle: (material) =>
            !isMixed || hasValidMaterialPercentages(material.combinationOf)
              ? null
              : {
                  background: 'status.dangerBackground',
                  borderLeft: '3px solid',
                  style: {
                    borderLeftColor: 'var(--chakra-colors-status-danger)',
                  },
                },
        },
        variant: 'entity-directory',
      }}
      error={error}
      pagination={{
        pageSize,
        totalItems: materials?.totalItems ?? 0,
        currentPage: page,
        onPageChange,
      }}
    />
  );
});
